import ReplayIcon from "@mui/icons-material/Replay";
import { Box, Button, useTheme } from "@mui/material";
import { useGridApiContext } from "@mui/x-data-grid-premium";
import React from "react";

import { useTenant } from "hooks/settings";

import {
  DeploymentType,
  generatedApi,
  Latest,
  Next,
  useUpdateAndDeployPlanResultsMutation,
} from "src/store/api/generatedApi";
import { AppDispatch, useAppDispatch } from "src/store/store";

import { useTenantTranslation } from "hooks/formatters";
import { RevertMixPopover } from "./RevertMixPopover";
import { disableEditEventParam, enableEditEventParam } from "./utils";
import { CoreDeploymentType } from "./types";

interface Props {
  mixId: number;
  deploymentType: DeploymentType;
  planId: number;
  startAt: Date;
  endAt: Date;
  planTimeframe: Latest | Next;
  originalDeploymentMixId: number;
}

interface BodyProps {
  mixId: number;
  deploymentType: DeploymentType;
  planId: number;
  startAt: Date;
  endAt: Date;
  planTimeframe: Latest | Next;
  broadDeploymentTypes:
    ["standard", "edited_standard"] | ["backup", "edited_backup"];
  reversionTargetMixId: number;
}

const useHandleOnSubmit = (
  reversionTargetMixId: number,
  mixId: number,
  deploymentType: DeploymentType,
  deploymentTypes: DeploymentType[],
  planTimeframe: Latest | Next,
  startAt: Date,
  endAt: Date,
  planId: number
) => {
  const tenantName = useTenant();
  const dispatch = useAppDispatch();
  const [
    doUpdateAndDeployPlanResultsMutation,
    updateAndDeployPlanResultsMutationResult,
  ] = useUpdateAndDeployPlanResultsMutation({ fixedCacheKey: "__revert__" });

  const callback = React.useCallback(() => {
    return doSubmit(
      reversionTargetMixId,
      mixId,
      deploymentType,
      deploymentTypes,
      planTimeframe,
      startAt,
      endAt,
      planId,
      doUpdateAndDeployPlanResultsMutation,
      dispatch,
      tenantName
    );
    // eslint-disable-next-line @eslint-react/exhaustive-deps -- deploymentType is derived from deploymentTypes and does not independently change; including it would cause redundant recalculations
  }, [
    reversionTargetMixId,
    mixId,
    deploymentTypes,
    planTimeframe,
    startAt,
    endAt,
    planId,
    doUpdateAndDeployPlanResultsMutation,
    dispatch,
    tenantName,
  ]);

  return [callback, updateAndDeployPlanResultsMutationResult] as const;
};

const doSubmit = async (
  reversionTargetMixId: number,
  mixId: number,
  deploymentType: DeploymentType,
  deploymentTypes: DeploymentType[],
  planTimeframe: Latest | Next,
  startAt: Date,
  endAt: Date,
  planId: number,
  doUpdate: ReturnType<typeof useUpdateAndDeployPlanResultsMutation>[0],
  dispatch: AppDispatch,
  tenantName: string
) => {
  const mixes = await doUpdate({
    tenantName,
    planId,
    deployedPlanMixUpdates: {
      period_updates: [
        {
          period: 1,
          updates: [],
          replacements: {
            [mixId]: reversionTargetMixId,
          },
        },
      ],
      layer_source: "per_basket_totals",
      deployment_type: deploymentType,
      start_at: startAt.toISOString(),
      end_at: endAt.toISOString(),
    },
  }).unwrap();

  dispatch(
    generatedApi.util.updateQueryData(
      "getDeployedPlanResults",
      {
        tenantName,
        deploymentTypes,
        planId: planTimeframe,
        period: 1,
      },
      () => mixes
    )
  );
};

export const RevertableMixButton = ({
  planId,
  mixId,
  startAt,
  endAt,
  deploymentType,
  planTimeframe,
  originalDeploymentMixId,
}: Props) => {
  if (deploymentType === "purchasing_plan") {
    return null;
  }
  return (
    <RevertableMixButtonContent
      planId={planId}
      mixId={mixId}
      startAt={startAt}
      endAt={endAt}
      deploymentType={deploymentType}
      planTimeframe={planTimeframe}
      originalDeploymentMixId={originalDeploymentMixId}
    />
  );
};

const RevertableMixButtonContent = ({
  planId,
  mixId,
  startAt,
  endAt,
  deploymentType,
  planTimeframe,
  originalDeploymentMixId,
}: Omit<Props, "deploymentType"> & {
  deploymentType: CoreDeploymentType;
}) => {
  const broadDeploymentTypes = React.useMemo(():
    ["standard", "edited_standard"] | ["backup", "edited_backup"] => {
    switch (deploymentType) {
      case "edited_standard":
      case "standard":
        return ["standard", "edited_standard"];
      case "backup":
      case "edited_backup":
        return ["backup", "edited_backup"];
    }
  }, [deploymentType]);

  if (originalDeploymentMixId !== mixId) {
    return (
      <Body
        planId={planId}
        mixId={mixId}
        startAt={startAt}
        endAt={endAt}
        deploymentType={deploymentType}
        planTimeframe={planTimeframe}
        reversionTargetMixId={originalDeploymentMixId}
        broadDeploymentTypes={broadDeploymentTypes}
      />
    );
  }
};

const Body = ({
  planId,
  mixId,
  startAt,
  endAt,
  deploymentType,
  planTimeframe,
  reversionTargetMixId,
  broadDeploymentTypes,
}: BodyProps) => {
  const theme = useTheme();
  const tenantName = useTenant();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const gridAPI = useGridApiContext();

  const [doSubmit, submissionResult] = useHandleOnSubmit(
    reversionTargetMixId,
    mixId,
    deploymentType,
    broadDeploymentTypes,
    planTimeframe,
    startAt,
    endAt,
    planId
  );

  const isLoading = submissionResult.isLoading;

  const handleConfirm = async () => {
    setIsSubmitting(true);
    gridAPI.current.publishEvent("stateChange", disableEditEventParam);
    await doSubmit();
    setIsSubmitting(false);
    setAnchorEl(null);
    gridAPI.current.publishEvent("stateChange", enableEditEventParam);
  };

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { t } = useTenantTranslation();

  return (
    <Box>
      <Button
        onClick={handleClick}
        disabled={isSubmitting || isLoading}
        variant="outlined"
        color="primary"
        size="small"
        startIcon={
          <ReplayIcon
            sx={{
              ...(isSubmitting
                ? {
                    animation: "spin 800ms infinite linear",
                    fill: theme.palette.primary.main,
                  }
                : {}),
              "@keyframes spin": {
                from: {
                  transform: "rotate(360deg)",
                },
                to: {
                  transform: "rotate(0deg)",
                },
              },
            }}
          />
        }
        sx={{
          minWidth: "100px",
          textTransform: "none",
          fontWeight: "fontWeightMedium",
          "&:hover": {
            backgroundColor: theme.palette.primary.main,
            color: theme.palette.primary.contrastText,
          },
        }}
      >
        {t("reset")}
      </Button>
      <RevertMixPopover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        onConfirm={() => void handleConfirm()}
        isLoading={isSubmitting}
        mixId={mixId}
        reversionTargetMixId={reversionTargetMixId}
        tenantName={tenantName}
        showDifferences={false}
      />
    </Box>
  );
};
